import { FC } from 'react';
import AddEditDocument from '../../../../components/addEditDocument/addEditDocument';
import {
  useStudentDocumentsState,
  useStudentDocumentsActions,
} from '../../contexts/studentDocuments.context';
import { TDocument, TDocumentOwnerType } from '../../../../../apis/documents/documents.interfaces';
import { useCreateDocumentMutationHook } from '../../hooks/useCreateDocumentMutationHook';
import { localStorageGet } from 'src/utils/localStorage';
import { ILoggedInUser } from 'src/apis/auth/auth.interfaces';

const StudentManageDocumentView: FC = () => {
  const {
    studentDocumentsAppState: {
      selectedDocument,
      isManageDocumentOpen,
      isDocumentLoading,
      documentErrorMessage,
      isCreateMode,
      isEditMode,
    },
  } = useStudentDocumentsState();

  const {
    setIsManageDocumentOpen,
    addDocument,
    updateDocument,
    setSelectedDocument,
    setIsCreateMode,
    setIsEditMode,
    setIsDocumentLoading,
    setDocumentErrorMessage,
  } = useStudentDocumentsActions();

  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const loggedInUserId = userFromStorage?.id || '';

  const createDocumentMutation = useCreateDocumentMutationHook();

  const handleClose = () => {
    setIsManageDocumentOpen(false);
    setSelectedDocument(null);
    setIsCreateMode(false);
    setIsEditMode(false);
    setIsDocumentLoading(false);
    setDocumentErrorMessage('');
  };

  const handleSubmit = (document: TDocument) => {
    if (isCreateMode) {
      setIsDocumentLoading(true);
      createDocumentMutation.mutate(
        {
          name: document.documentName,
          description: document.description,
          ownerType: TDocumentOwnerType.STUDENT,
          ownerId: document.userId,
          createdBy: loggedInUserId,
        },
        {
          onSuccess: () => {
            addDocument(document);
            handleClose();
          },
          onError: (error) => {
            setIsDocumentLoading(false);
            setDocumentErrorMessage(error?.response?.data?.message || 'Failed to create document');
          },
        }
      );
    } else if (isEditMode) {
      updateDocument(document);
      handleClose();
    }
  };

  const onUploadProgress = (status: boolean, message: string) => {
    setIsDocumentLoading(status);
    setDocumentErrorMessage(message);
  };

  return (
    <div data-testid="student-manage-document-container">
      <AddEditDocument
        isOpen={isManageDocumentOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        onUploadProgress={onUploadProgress}
        document={selectedDocument ?? null}
        isLoading={isDocumentLoading || createDocumentMutation.isPending}
        errorMessage={documentErrorMessage}
      />
    </div>
  );
};

export default StudentManageDocumentView;
